import { useEffect, useRef } from "react";
import type { TrackEvent } from "@salestrak/analytics-taxonomy";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { useAnalytics } from "./analytics-provider";

/**
 * Tracks ONE analytics event per *committed* search query, not per keystroke.
 * Debounces `value`, then fires `event()` once when the trimmed term settles to
 * a new non-empty value — so typing "roof" emits a single `search` event and
 * clearing the box emits nothing.
 *
 * `event` is a factory returning the caller-specific {@link TrackEvent} (so the
 * caller stamps its own `context`/`data`); it's read through a ref so callers can
 * pass an inline arrow without churning the effect deps. Reused by the issue
 * board/list and the teams list — the debounce lives here once, not per screen.
 */
export function useTrackSearch(value: string, event: () => TrackEvent): void {
  const { track } = useAnalytics();
  const debounced = useDebouncedValue(value, 500);
  const eventRef = useRef(event);
  eventRef.current = event;
  const lastTracked = useRef("");

  useEffect(() => {
    const term = debounced.trim();
    if (!term || term === lastTracked.current) return;
    lastTracked.current = term;
    track(eventRef.current());
  }, [debounced, track]);
}
